import {
  CustomPeer,
  getPeersQueryKey,
  QueryPeersResponse,
} from '@/pages/CompanyReport/components/Valuation/query';
import { Modal, Text } from '@simplywallst/ui-core';
import { GridBox, ModalWrapper, ResetLink } from './styled';
import { useState } from 'react';
import { initCustomPeers } from '../../../../MultipleVsPeers';
import { useQueryClient } from '@tanstack/react-query';
import PeerSearch from '../PeerSearch';
import { useTracking } from 'react-tracking';
import { PEERS_LIMIT } from '@/pages/CompanyReport/constants';
import {
  useValuationTranslation,
  ValuationTranslation,
} from '@/pages/CompanyReport/components/Valuation/hooks/useValuationTranslation';

type Props = {
  currentPeers: CustomPeer[];
  uniqueSymbol: string;
  canonicalUrl?: string;
  onSubmitCustomPeers: (peers: CustomPeer[]) => void;
  onUpdatePeers: (isCustomPeerSelected: boolean) => void;
  onResetAllPeers: () => void;
  onClose: () => void;
};

export const CustomPeersModal = ({
  currentPeers,
  uniqueSymbol,
  canonicalUrl,
  onSubmitCustomPeers,
  onUpdatePeers,
  onResetAllPeers,
  onClose,
}: Props) => {
  const { trackEvent } = useTracking();
  const [selectingPeers, setSelectingPeers] = useState<CustomPeer[]>([
    ...currentPeers,
  ]);
  const [inputError, setInputError] = useState<boolean[]>([
    false,
    false,
    false,
    false,
  ]);
  const queryClient = useQueryClient();

  const handleSelectPeer = (index: number, newPeer: CustomPeer) => {
    setSelectingPeers((prev) => {
      const peers = [...prev];
      peers[index] = newPeer;
      return peers;
    });
    onUpdatePeers(true);
  };
  const handleResetPeer = (index: number) => {
    setSelectingPeers((prev) => {
      const peers = [...prev];
      peers[index] = initCustomPeers[index];
      return peers;
    });
    onUpdatePeers(true);
  };
  const handleResetAllPeers = () => {
    const defaultPeers = queryClient.getQueryData<QueryPeersResponse>(
      getPeersQueryKey(canonicalUrl, PEERS_LIMIT, [])
    );
    onResetAllPeers();
    if (defaultPeers === undefined) return;
    setSelectingPeers([...defaultPeers.customPeers]);
    setInputError([false, false, false, false]);
    onUpdatePeers(false);
    trackEvent({
      action: 'reset',
      modifier: 'Edit Peers',
      uniqueSymbol: uniqueSymbol,
      customPeers: JSON.stringify(defaultPeers.customPeers),
    });
  };
  const handleSubmitPeers = () => {
    if (inputError.find((error) => error === true) === undefined)
      onSubmitCustomPeers(selectingPeers);
  };
  const handleOnError = (index: number, isError: boolean) => {
    setInputError((prev) => {
      const errors = [...prev];
      errors[index] = isError;
      return errors;
    });
  };

  const t = useValuationTranslation();

  return (
    <Modal
      title={
        <Text typography="large" color="spaceGray">
          {t('vsPeers.customPeers.edit')}
        </Text>
      }
      asForm
      onClose={onClose}
      onPrimaryClick={handleSubmitPeers}
      primaryLabel={t('vsPeers.customPeers.submit')}
      secondaryLabel={t('vsPeers.customPeers.cancel')}
    >
      <ModalWrapper>
        <Text typography="xSmall" color="phantom">
          <ValuationTranslation
            i18nKey="vsPeers.customPeers.description"
            values={{ uniqueSymbol }}
          >
            <Text as="span" typography="strong">
              {uniqueSymbol}
            </Text>
          </ValuationTranslation>
        </Text>
        <GridBox gap="8px">
          {selectingPeers.map((peer, i) => (
            <PeerSearch
              key={i}
              index={i}
              peer={peer}
              isError={inputError[i]}
              selectingPeers={selectingPeers}
              uniqueSymbol={uniqueSymbol}
              onError={handleOnError}
              onSelectPeer={handleSelectPeer}
              onResetPeer={handleResetPeer}
            />
          ))}
        </GridBox>
        <ResetLink data-cy-id="reset-link" onClick={handleResetAllPeers}>
          {t('vsPeers.customPeers.reset')}
        </ResetLink>
      </ModalWrapper>
    </Modal>
  );
};
